import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ConfigProvider, Tabs } from 'antd';
import { selectData } from '../../state/machineDetailsPanelSelectors';
import { MachineDetailsRobotCleaningData } from '../MachineDetailsRobotPanel/MachineDetailsRobotCleaningData/MachineDetailsRobotCleaningData';
import { MachineDetailsRobotOverview } from '../MachineDetailsRobotPanel/MachineDetailsRobotOverview/MachineDetailsRobotOverview';
import { MachineDetailsRobotSchedule } from '../MachineDetailsRobotPanel/MachineDetailsRobotSchedule/MachineDetailsRobotSchedule';
import { MachineDetailsPosition } from '../MachineDetailsPosition/MachineDetailsPosition';
import { StyledMachineDetailsPanelRobot } from './MachineDetailsPanelRobot.styles';
import { useAnalyticsSetProductInfo } from 'app/cross-cutting-concerns/analytics/hooks/useAnalyticsSetProductInfo';
import { useAnalyticsSetPageInfo } from 'app/cross-cutting-concerns/analytics/hooks/useAnalyticsSetPageInfo';
import { PageNamePrefix } from 'config/pages';
import { isProductionEnvironment } from 'config/environment';
import { Machine } from 'app/modules/machine-inventory/interfaces/Machine.types';
import { MachineAttachmentList } from 'app/modules/machine-inventory/machine-details/components/MachineAttachmentList/MachineAttachmentList';
import { FeatureFlagSelectors } from 'app/cross-cutting-concerns/feature-flags/state/featureFlagSelectors';
import { RobotUtils } from 'app/utils/robot/RobotUtils';
import { Styling } from 'app/cross-cutting-concerns/styling/Styling';
import { DependencyInjection } from 'app/cross-cutting-concerns/dependency-injection/DependencyInjection';

enum MachineDetailsRobotTabs {
  OVERVIEW = 'OVERVIEW',
  CLEANING_DATA = 'CLEANING_DATA',
  SCHEDULE = 'SCHEDULE',
  DOCUMENTS = 'DOCUMENTS',
  POSITION_HISTORY = 'POSITION_HISTORY',
}

export const MachineDetailsPanelRobot = (): React.JSX.Element => {
  const { t } = useTranslation();
  const robot = useSelector(selectData);
  const features = useSelector(FeatureFlagSelectors.selectFeatureFlagConfig);
  const styling = DependencyInjection.resolve<Styling>(Styling);
  const kccAntdTheme = styling.getAntDesignKCCThemeConfig();

  useAnalyticsSetProductInfo({
    productName: robot?.name,
    productMaterialNo: robot?.materialNumber,
    productSerialNo: robot?.serialNumber,
  });
  useAnalyticsSetPageInfo({
    // eslint-disable-next-line max-len
    pageName: `${PageNamePrefix.MACHINE_DETAILS}${robot?.name} ${robot?.classification} (${robot?.materialNumber} / ${robot?.serialNumber})`,
  });

  const isRobotCV50Enabled = features.ROBOTICS_CV50;
  const showPositionHistory = features.MACHINE_POSITION_HISTORY;
  const showScheduleTab = isProductionEnvironment ? features.ROBOT_SCHEDULE_TAB : true;

  const isCV50Robot = robot && RobotUtils.getRobotType(robot?.type?.name) === 'CV50';

  const tabs = [
    {
      label: t('machineDetailsPanel.tabs.cleaningData'),
      key: MachineDetailsRobotTabs.CLEANING_DATA,
      children: <MachineDetailsRobotCleaningData robot={robot as Machine} />,
    },
    ...(showScheduleTab
      ? [
          {
            label: t('machineDetailsPanel.tabs.schedule'),
            key: MachineDetailsRobotTabs.SCHEDULE,
            children: (
              <ConfigProvider
                theme={kccAntdTheme}
                table={{ className: 'kcc-theme-table' }}
                rangePicker={{ className: 'kcc-theme-range-picker' }}
                input={{ className: 'kcc-theme-input' }}
                inputNumber={{ className: 'kcc-theme-input-number' }}
                select={{ className: 'kcc-theme-select' }}
                datePicker={{ className: 'kcc-theme-date-picker' }}
                timePicker={{ className: 'kcc-theme-time-picker' }}
                switch={{ className: 'kcc-theme-switch' }}
                segmented={{ className: 'kcc-theme-segmented' }}
              >
                <MachineDetailsRobotSchedule robot={robot as Machine} />
              </ConfigProvider>
            ),
          },
        ]
      : []),
    {
      label: t('machineDetailsPanel.tabs.overview'),
      key: MachineDetailsRobotTabs.OVERVIEW,
      children: <MachineDetailsRobotOverview robot={robot as Machine} />,
    },
    {
      label: t('machineDetailsPanel.tabs.documents'),
      key: MachineDetailsRobotTabs.DOCUMENTS,
      children: <MachineAttachmentList machine={robot as Machine} size="full-width" />,
    },
    ...(robot?.isIoTDevice && showPositionHistory
      ? [
          {
            label: t('machineDetailsPanel.tabs.positionHistory'),
            key: MachineDetailsRobotTabs.POSITION_HISTORY,
            children: (
              <ConfigProvider
                theme={kccAntdTheme}
                table={{ className: 'kcc-theme-table' }}
                rangePicker={{ className: 'kcc-theme-range-picker' }}
              >
                <MachineDetailsPosition machineId={robot.id} />
              </ConfigProvider>
            ),
          },
        ]
      : []),
  ];

  const filteredTabs = tabs.slice(1);

  const tabsToRender = !isRobotCV50Enabled && isCV50Robot ? filteredTabs : tabs;

  return (
    <StyledMachineDetailsPanelRobot>
      {robot ? (
        <Tabs
          id="machine-panel-robot-tabs"
          destroyInactiveTabPane={true}
          items={tabsToRender}
          className="machine-panel-robot__tabs"
        />
      ) : (
        t('machineDetails.error.machineNotFound')
      )}
    </StyledMachineDetailsPanelRobot>
  );
};
